import React, { useId, useLayoutEffect, useRef, useState } from 'react';
import {
  ARCA_MOZZO_REPRESENTATIVE_CUIT,
  ARCA_RELATION_MANAGER_URL,
  formatCuit,
  isValidCuit,
} from '../../services/local/fiscalConnection';
import {
  ARCA_DELEGATION_GUIDE,
  ARCA_DELEGATION_IMAGES,
  buildArcaAccountantInstructions,
  hasSavedGuideIssuer,
} from '../../services/local/arcaDelegationGuide';
import { copyTextToClipboard } from '../../services/local/invitationLinks';
import '../../styles/ArcaDelegationGuide.css';

function ArcaDelegationGuideContent({ issuer, environment, representativeCuit, onOpenIssuer, canEditIssuer }) {
  const id = useId();
  const titleRef = useRef(null);
  const focusNextStep = useRef(false);
  const role = issuer?.metadata?.onboardingContact?.operatorRole || '';
  const [stepIndex, setStepIndex] = useState(0);
  const [copyMessage, setCopyMessage] = useState('');
  const [copying, setCopying] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const guide = ARCA_DELEGATION_GUIDE;
  const step = guide.steps[stepIndex];
  const savedIssuer = hasSavedGuideIssuer(issuer);
  const validRepresentative = isValidCuit(representativeCuit);
  const instructions = buildArcaAccountantInstructions({ issuer, representativeCuit, environment });
  const capture = ARCA_DELEGATION_IMAGES[step.imageKey];

  useLayoutEffect(() => {
    if (!focusNextStep.current) return;
    focusNextStep.current = false;
    titleRef.current?.focus({ preventScroll: true });
    titleRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'auto' });
  }, [stepIndex]);

  const goToStep = (index) => {
    if (index === stepIndex) return;
    focusNextStep.current = true;
    setStepIndex(index);
  };

  const copy = async (text, successMessage) => {
    setCopying(true);
    setCopyMessage('');
    try {
      if (!await copyTextToClipboard(text)) throw new Error('Clipboard unavailable');
      setCopyMessage(successMessage);
    } catch {
      setCopyMessage('No pudimos copiar automaticamente. Podes seleccionar y copiar el texto que se muestra.');
    } finally {
      setCopying(false);
    }
  };

  if (environment !== 'production') {
    return (
      <div className="arca-guide">
        <div className="arca-guide__notice">
          <strong>Estas en homologacion, no en produccion.</strong>
          <p>Esta guia describe la delegacion real del restaurante. La autorizacion de pruebas se gestiona por separado mediante WSASS y no habilita comprobantes reales.</p>
          <p>Para consultar la guia del restaurante, selecciona Produccion arriba. Cambiar esa vista no activa la facturacion.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="arca-guide">
      <p className="arca-guide__intro">Tu restaurante sigue siendo el emisor de las facturas. MozzoApp solo operara el servicio tecnico de Facturacion Electronica que autorices.</p>
      <div className="arca-guide__security"><strong>Nunca compartas tu clave fiscal.</strong> Se ingresa unicamente en el sitio oficial de ARCA, no en MozzoApp.</div>
      {!savedIssuer && <p className="arca-guide__notice">Podes explorar las instrucciones, pero no realices la delegacion hasta guardar y revisar el CUIT y la razon social del emisor.</p>}
      {!validRepresentative && <p className="arca-guide__notice">No continues en ARCA: MozzoApp debe configurar su CUIT representante antes de que puedas autorizarlo.</p>}

      <button type="button" className="arca-guide__details-toggle" aria-expanded={showDetails} aria-controls={`${id}-details`} onClick={() => setShowDetails(!showDetails)}>
        <span>Datos del tramite e instrucciones para mi contador</span><span aria-hidden="true">{showDetails ? '-' : '+'}</span>
      </button>
      {showDetails && <div id={`${id}-details`} className="arca-guide__details">
      <div className="arca-guide__roles">
        <strong>Persona registrada para el tramite</strong>
        <p>{issuer?.metadata?.onboardingContact?.operatorName || 'Por completar en Datos del emisor.'}{role ? ` / ${guide.roles.find((option) => option.id === role)?.label || ''}` : ''}</p>
        <p>Debe ser Administrador o Subadministrador de Relaciones del CUIT emisor. Registrar estos datos no otorga permisos ni envia avisos.</p>
      </div>

      <div className="arca-guide__identities">
        <div className="arca-guide__identity">
          <span className="arca-guide__eyebrow">Restaurante / representado</span>
          <strong>{savedIssuer ? formatCuit(issuer.cuit) : 'Datos pendientes'}</strong>
          <span>{savedIssuer ? issuer.legalName : 'Guarda primero los datos del emisor en el paso 1.'}</span>
          <small>Es quien emite la factura al cliente.</small>
          <small>Este recuadro es un resumen de los datos guardados, no un formulario.</small>
          {onOpenIssuer && <button type="button" onClick={onOpenIssuer}>{canEditIssuer ? 'Completar o editar datos' : 'Ver datos fiscales'}</button>}
        </div>
        <div className="arca-guide__identity arca-guide__identity--representative">
          <span className="arca-guide__eyebrow">MozzoApp / representante tecnico</span>
          <strong>{validRepresentative ? formatCuit(representativeCuit) : 'CUIT no configurado'}</strong>
          <span>Autorizado solo para Facturacion Electronica.</span>
          <button type="button" disabled={!validRepresentative || copying} onClick={() => copy(representativeCuit, 'CUIT de MozzoApp copiado.')}>Copiar CUIT de MozzoApp</button>
        </div>
      </div>
      <div className="arca-guide__tools">
        <button type="button" disabled={!instructions || copying} onClick={() => {
          setShowInstructions(true);
          copy(instructions, 'Instrucciones copiadas. Podes enviarselas a tu contador; MozzoApp no las envio.');
        }}>Copiar instrucciones para mi contador</button>
        <a href={ARCA_RELATION_MANAGER_URL} target="_blank" rel="noopener noreferrer">Ingresar al sitio oficial de ARCA</a>
      </div>
      {role && role !== 'owner' && <p className="arca-guide__hint">Compartile las instrucciones a {role === 'accountant' ? 'tu contador' : 'la persona autorizada'}. No necesita tu usuario de MozzoApp ni debe enviarte su clave fiscal.</p>}
      <div role="status" aria-live="polite" className="arca-guide__copy-status">{copyMessage}</div>
      {showInstructions && instructions && (
        <div className="arca-guide__copy-panel">
          <label htmlFor={`${id}-instructions`}>Instrucciones para compartir (datos fiscales guardados)</label>
          <textarea id={`${id}-instructions`} readOnly value={instructions} rows={7} />
          <button type="button" onClick={() => setShowInstructions(false)}>Ocultar instrucciones</button>
        </div>
      )}
      </div>}

      <nav aria-label="Pasos de la guia de delegacion" className="arca-guide__steps">
        <ol>
          {guide.steps.map((item, index) => (
            <li key={item.id}><button type="button" aria-current={index === stepIndex ? 'step' : undefined}
              aria-label={`Paso ${index + 1}: ${item.title}`} title={item.title} onClick={() => goToStep(index)}>{index + 1}</button></li>
          ))}
        </ol>
      </nav>
      <section className="arca-guide__instruction" aria-labelledby={`${id}-step-title`}>
        <span className="arca-guide__eyebrow">Guia de produccion / paso {stepIndex + 1} de {guide.steps.length}</span>
        <h3 ref={titleRef} tabIndex={-1} id={`${id}-step-title`}>{step.title}</h3>
        <p>{step.instruction}</p>
        {step.id === 'login' && <a className="arca-guide__official-link" href={ARCA_RELATION_MANAGER_URL} target="_blank" rel="noopener noreferrer">Ingresar al sitio oficial de ARCA</a>}
        {step.id === 'service' && <strong className="arca-guide__service">{guide.service}</strong>}
        {(step.showSummary || step.id === 'representative') && (
          <dl className="arca-guide__summary">
            <div><dt>Representado</dt><dd>{savedIssuer ? `${formatCuit(issuer.cuit)} - ${issuer.legalName}` : 'Falta guardar los datos fiscales del restaurante.'}</dd></div>
            <div><dt>Servicio</dt><dd>{guide.service}</dd></div>
            <div><dt>Representante tecnico</dt><dd>{validRepresentative ? `MozzoApp - ${formatCuit(representativeCuit)}` : 'Pendiente de configuracion'}</dd></div>
          </dl>
        )}
        <aside className="arca-guide__notice">{step.warning}</aside>
        {capture?.src && capture?.alt && (
          <figure className="arca-guide__capture">
            <img src={capture.src} alt={capture.alt} loading="lazy" />
            <figcaption>{capture.caption}</figcaption>
          </figure>
        )}
        <div className="arca-guide__navigation">
          <button type="button" disabled={stepIndex === 0} onClick={() => goToStep(stepIndex - 1)}>Anterior</button>
          <span>{stepIndex === guide.steps.length - 1 ? 'Fin de las instrucciones' : `${stepIndex + 1} / ${guide.steps.length}`}</span>
          {stepIndex < guide.steps.length - 1
            ? <button type="button" className="arca-guide__next" onClick={() => goToStep(stepIndex + 1)}>Siguiente</button>
            : <button type="button" onClick={() => goToStep(0)}>Volver al inicio</button>}
        </div>
      </section>
      <p className="arca-guide__footnote">Este recorrido es informativo: no comprueba acciones realizadas en ARCA ni cambia el estado de tu conexion. <a href={guide.sourceUrl} target="_blank" rel="noopener noreferrer">Consultar la guia oficial de ARCA (PDF)</a>. Las pantallas pueden cambiar.</p>
    </div>
  );
}

export default function ArcaDelegationGuide({ companyId, issuer, environment = 'production', onOpenIssuer, canEditIssuer = false }) {
  const scopedIssuer = issuer?.companyId === companyId && issuer?.environment === environment ? issuer : null;
  return <ArcaDelegationGuideContent key={`${companyId}:${environment}:${scopedIssuer?.id || ''}:${scopedIssuer?.cuit || ''}:${scopedIssuer?.legalName || ''}`}
    issuer={scopedIssuer} environment={environment} representativeCuit={ARCA_MOZZO_REPRESENTATIVE_CUIT}
    onOpenIssuer={onOpenIssuer} canEditIssuer={canEditIssuer} />;
}
